import { FormProvider, getFormProps } from '@conform-to/react'
import { DropdownMenu, Flex } from '@radix-ui/themes'
import { useLocation } from '@remix-run/react'
import * as React from 'react'
import { RxBookmark } from 'react-icons/rx'
import { z } from 'zod'
import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { SubmissionButton } from '#app/components/common/submission-button.tsx'
import { type Locale } from '#app/models/locale.ts'
import { type FormEnum } from '#app/routes/_layout/route-config.ts'
import { generateItemFormFetcherKey } from '#app/utils/client/generate-item-form-fetcher-key.ts'
import { generateItemFormId } from '#app/utils/client/generate-item-form-id.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useSetDefaultAddressForm } from '#app/utils/hooks/forms/use-set-default-address-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Set default address form
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	defaultAddress = 'default-address',
}

const FORM: FormEnum = {
	generateId: ({ id }) => generateItemFormId('set-default-address-form', id),
	action: '/profile/addresses',
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('set-default-address-form', id),
	subaction: 'set-default-address-form',
	redirect: '/profile/addresses',
} as const

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.defaultAddress]: z.string({
			required_error: 'validation.email.required',
		}),
	},
})

type SetDefaultAddressFormProps = {
	id: number
	iri: string
	locale: Locale
}

const SetDefaultAddressForm: React.FC<SetDefaultAddressFormProps> = ({
	id,
	iri,
	locale,
}) => {
	const {
		defaultAddressFetcher,
		defaultAddressFormValues,
		defaultAddressForm,
		defaultAddressFields,
		t,
	} = useSetDefaultAddressForm({ id, iri })

	const location = useLocation()

	return (
		<FormProvider context={defaultAddressForm.context}>
			<DropdownMenu.Item asChild>
				<defaultAddressFetcher.Form
					method="post"
					action={`/${locale}${FORM.action}${location.search}`}
					preventScrollReset
					{...getFormProps(defaultAddressForm)}
					className="gap-0"
				>
					<FormDefaults
						form={defaultAddressForm}
						fields={defaultAddressFields}
						values={defaultAddressFormValues}
					/>
					<Flex asChild align="center" justify="between" gapX="4">
						<button type="submit">
							<SubmissionButton fetcher={defaultAddressFetcher}>
								{t('items.button.setAsDefaultAddressSubmit')}
								<RxBookmark />
							</SubmissionButton>
						</button>
					</Flex>
				</defaultAddressFetcher.Form>
			</DropdownMenu.Item>
		</FormProvider>
	)
}
SetDefaultAddressForm.displayName = 'SetDefaultAddressForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type SetDefaultAddressFormProps,
	SetDefaultAddressForm,
	FORM as SET_DEFAULT_ADDRESS_FORM,
	schema as setDefaultAddressSchema,
	INPUTS as SET_DEFAULT_ADDRESS_INPUTS,
}
